import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { 
  Calendar as CalendarIcon, ArrowUpRight, ArrowDownRight, 
  Construction, HardHat, User, SlidersHorizontal, RefreshCcw, FileText, Landmark
} from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";

export default function DailyLogsUnified() {
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("RANGE"); // SINGLE, RANGE, FULL
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [unifiedLedger, setUnifiedLedger] = useState([]);
  
  // Overall aggregated stats for the selected date window
  const [totals, setTotals] = useState({
    netSiteExpenses: 0,
    netSupervisorExpenses: 0,
    netPersonalIncome: 0,
    netPersonalExpenses: 0,
  });

  const fetchUnifiedData = useCallback(async () => {
    try {
      setLoading(true);

      // 1. Initialize queries
      let dailyQuery = supabase.from("daily_logs").select("*, sites(site_name), supervisors(name)");
      let personalQuery = supabase.from("personal_expenses").select("*");
      let supervisorQuery = supabase.from("supervisor_logs").select("*, supervisors(name)");

      // 2. Apply chronological boundaries
      if (filterType === "SINGLE") {
        dailyQuery = dailyQuery.eq("date", startDate);
        personalQuery = personalQuery.eq("date", startDate);
        // Casting or formatting check in case supervisor_logs uses full timestamps
        supervisorQuery = supervisorQuery.gte("date", `${startDate}T00:00:00`).lte("date", `${startDate}T23:59:59`);
      } else if (filterType === "RANGE") {
        dailyQuery = dailyQuery.gte("date", startDate).lte("date", endDate);
        personalQuery = personalQuery.gte("date", startDate).lte("date", endDate);
        supervisorQuery = supervisorQuery.gte("date", `${startDate}T00:00:00`).lte("date", `${endDate}T23:59:59`);
      }

      // 3. Resolve concurrent requests
      const [dailyRes, personalRes, supervisorRes] = await Promise.all([
        dailyQuery.order("date", { ascending: false }),
        personalQuery.order("date", { ascending: false }),
        supervisorQuery.order("date", { ascending: false }),
      ]);

      const dailyLogs = dailyRes.data || [];
      const personalExpenses = personalRes.data || [];
      const supervisorLogs = supervisorRes.data || [];

      // 4. Group all heterogeneous datasets datewise
      const dateMap = {};

      const getInitializedDay = (dateKey) => {
        const standardDate = format(new Date(dateKey), "yyyy-MM-dd");
        if (!dateMap[standardDate]) {
          dateMap[standardDate] = {
            date: standardDate,
            siteLogs: [],
            supLogs: [],
            personalItems: [],
            dayTotals: { siteOut: 0, supOut: 0, personalIn: 0, personalOut: 0 }
          };
        }
        return dateMap[standardDate];
      };

      // Process Site Operations Logs
      dailyLogs.forEach(log => {
        const day = getInitializedDay(log.date);
        day.siteLogs.push(log);
        day.dayTotals.siteOut += Number(log.total_expenses || 0);
      });

      // Process Supervisor Operations Logs
      supervisorLogs.forEach(log => {
        const day = getInitializedDay(log.date);
        day.supLogs.push(log);
        const logExp = Number(log.food_expenses || 0) + 
                       Number(log.room_expenses || 0) + 
                       Number(log.travel_expenses || 0) + 
                       Number(log.other_expenses || 0) +
                       Number(log.advance_taken || 0) +
                       Number(log.salary_credited || 0);
        day.dayTotals.supOut += logExp;
      });

      // Process Personal Account Ledger
      personalExpenses.forEach(item => {
        const day = getInitializedDay(item.date);
        day.personalItems.push(item);
        if (item.type === "income") {
          day.dayTotals.personalIn += Number(item.amount || 0);
        } else {
          day.dayTotals.personalOut += Number(item.amount || 0);
        }
      });

      // 5. Compute structural list sorted descending by date
      const sortedDays = Object.values(dateMap).sort((a, b) => new Date(b.date) - new Date(a.date));

      // 6. Calculate total metrics over the entire filtered scope
      let globalSite = 0, globalSup = 0, globalPersIn = 0, globalPersOut = 0;
      sortedDays.forEach(d => {
        globalSite += d.dayTotals.siteOut;
        globalSup += d.dayTotals.supOut;
        globalPersIn += d.dayTotals.personalIn;
        globalPersOut += d.dayTotals.personalOut;
      });

      setUnifiedLedger(sortedDays);
      setTotals({
        netSiteExpenses: globalSite,
        netSupervisorExpenses: globalSup,
        netPersonalIncome: globalPersIn,
        netPersonalExpenses: globalPersOut
      });

    } catch (err) {
      console.error("Unified calculation failure:", err);
      toast.error("Failed to load global tracking ledger");
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate]);

  useEffect(() => {
    fetchUnifiedData();
  }, [fetchUnifiedData]);

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-16">
      
      {/* 🔷 CONTROL BAR STICKY HEADER */}
      <div className="bg-white border-b border-slate-200 px-4 py-5 lg:px-8 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#0054A6]">
              <Landmark size={22} />
              <h1 className="text-xl font-black tracking-tight text-slate-900 uppercase">Unified Day Ledger</h1>
            </div>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">Cross-Table Financial Verification System</p>
          </div>

          {/* DATE CONTROL PANEL UI */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-slate-100 p-1 rounded-xl flex border border-slate-200">
              {["SINGLE", "RANGE", "FULL"].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black transition-all ${
                    filterType === type ? "bg-white text-[#0054A6] shadow-xs" : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            {filterType !== "FULL" && (
              <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-xl border">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none text-xs font-bold text-slate-700 focus:outline-none p-1"
                />
                {filterType === "RANGE" && (
                  <>
                    <span className="text-slate-300 font-bold text-xs">to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-transparent border-none text-xs font-bold text-slate-700 focus:outline-none p-1"
                    />
                  </>
                )}
              </div>
            )}

            <button onClick={fetchUnifiedData} className="p-2.5 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-xl transition-colors">
              <RefreshCcw size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
        
        {/* 🔷 METRIC OVERVIEW DECK */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ScopeStatCard title="Site Cash Flow Out" val={totals.netSiteExpenses} color="text-blue-600" desc="Aggregate Project Spending" />
          <ScopeStatCard title="Sup Expenses Out" val={totals.netSupervisorExpenses} color="text-amber-600" desc="Advances, Salary & Payouts" />
          <ScopeStatCard title="Personal Income In" val={totals.netPersonalIncome} color="text-emerald-600" desc="Direct Capital Inflow" />
          <ScopeStatCard title="Personal Wallet Out" val={totals.netPersonalExpenses} color="text-red-500" desc="Independent Outflow" />
        </div>

        {/* 🔷 CHRONOLOGICAL TIMELINE LEDGER SHEET */}
        {loading ? (
          <div className="text-center py-20 font-black tracking-widest text-[#0054A6] animate-pulse text-xs uppercase">Compiling Cross-Table Assets...</div>
        ) : unifiedLedger.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center border border-slate-200 shadow-xs max-w-xl mx-auto">
            <FileText className="mx-auto text-slate-300 mb-4" size={40} />
            <h3 className="font-black text-slate-800 text-sm uppercase">No Records Tracked</h3>
            <p className="text-xs text-slate-400 font-medium mt-1">There are no operational logs recorded inside this selected filter timeframe.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {unifiedLedger.map((day) => {
              const netDayOutflow = day.dayTotals.siteOut + day.dayTotals.supOut + day.dayTotals.personalOut;
              const dayBalance = day.dayTotals.personalIn - netDayOutflow;

              return (
                <div key={day.date} className="bg-white rounded-[2rem] border border-slate-200 shadow-xs overflow-hidden transition-all hover:shadow-md">
                  
                  {/* DATE SECTION CARD HEADER HERO */}
                  <div className="bg-slate-900 px-6 py-4 flex flex-wrap justify-between items-center gap-4 text-white">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/10 p-2.5 rounded-xl text-blue-400">
                        <CalendarIcon size={18} />
                      </div>
                      <div>
                        <h2 className="text-base font-black tracking-tight">
                          {format(new Date(day.date), "dd MMMM, yyyy")}
                        </h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{format(new Date(day.date), "EEEE")}</p>
                      </div>
                    </div>

                    {/* Day-Wise Balances */}
                    <div className="flex flex-wrap items-center gap-4 bg-white/5 border border-white/10 px-4 py-2 rounded-2xl">
                      <DayInlineMetric label="Site Out" val={day.dayTotals.siteOut} color="text-blue-400" />
                      <DayInlineMetric label="Sup Out" val={day.dayTotals.supOut} color="text-amber-400" />
                      <DayInlineMetric label="Pers In" val={day.dayTotals.personalIn} color="text-emerald-400" />
                      <DayInlineMetric label="Pers Out" val={day.dayTotals.personalOut} color="text-red-400" />
                      <div className="border-l border-white/20 pl-4 ml-2">
                        <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Day Balance</p>
                        <p className={`text-xs font-black ${dayBalance >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {dayBalance >= 0 ? "+" : ""}₹{dayBalance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* SUB-TABLE EXPANSIONS CARD SECTION BODY */}
                  <div className="p-6 space-y-6 bg-slate-50/50">
                    
                    {/* 1. SITE LOGS MATRIX CONTAINER */}
                    {day.siteLogs.length > 0 && (
                      <div className="bg-white rounded-2xl border p-4 space-y-3 shadow-xs">
                        <div className="flex items-center gap-2 text-blue-600 font-black text-xs uppercase tracking-wider border-b pb-2">
                          <Construction size={14} /> Site Operations Logs
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {day.siteLogs.map(log => (
                            <div key={log.id} className="bg-slate-50 p-3 rounded-xl border flex justify-between items-center text-xs">
                              <div>
                                <p className="font-black text-slate-800 uppercase text-xs">{log.sites?.site_name || "Unknown Site"}</p>
                                <p className="text-[10px] text-slate-400 font-bold mt-0.5">
                                  Labor Base: <span className="text-slate-600">{log.labour_count} men</span> | Supervisor: {log.supervisors?.name || "None"}
                                </p>
                              </div>
                              <span className="font-black text-slate-900 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-100">
                                ₹{Number(log.total_expenses || 0).toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 2. SUPERVISOR LOGS MATRIX CONTAINER */}
                    {day.supLogs.length > 0 && (
                      <div className="bg-white rounded-2xl border p-4 space-y-3 shadow-xs">
                        <div className="flex items-center gap-2 text-amber-600 font-black text-xs uppercase tracking-wider border-b pb-2">
                          <HardHat size={14} /> Supervisor Field Ledger
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {day.supLogs.map(log => {
                            const breakDownSum = Number(log.food_expenses || 0) + Number(log.room_expenses || 0) + Number(log.travel_expenses || 0) + Number(log.other_expenses || 0);
                            return (
                              <div key={log.id} className="bg-slate-50 p-3 rounded-xl border text-xs flex flex-col justify-between gap-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-black text-slate-800 uppercase">{log.supervisors?.name || "Contract Supervisor"}</p>
                                    <p className="text-[10px] text-slate-400 font-bold italic mt-0.5">"{log.work_done_description || "Routine oversight updates"}"</p>
                                  </div>
                                  <span className={`text-[9px] px-2 py-0.5 font-black uppercase rounded-md ${log.attendance_status === 'Present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {log.attendance_status}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2 text-[10px] bg-white p-2 rounded-lg border border-slate-100 font-bold text-slate-500">
                                  {Number(log.food_expenses) > 0 && <span>Food: <b className="text-slate-700">₹{log.food_expenses}</b></span>}
                                  {breakDownSum > Number(log.food_expenses) && <span>Logistics: <b className="text-slate-700">₹{breakDownSum - log.food_expenses}</b></span>}
                                  {Number(log.advance_taken) > 0 && <span className="text-red-500">Advance Taken: <b>₹{log.advance_taken}</b></span>}
                                  {Number(log.salary_credited) > 0 && <span className="text-green-600">Salary Issued: <b>₹{log.salary_credited}</b></span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 3. PERSONAL CASH LEDGER MATRIX CONTAINER */}
                    {day.personalItems.length > 0 && (
                      <div className="bg-white rounded-2xl border p-4 space-y-3 shadow-xs">
                        <div className="flex items-center gap-2 text-emerald-600 font-black text-xs uppercase tracking-wider border-b pb-2">
                          <User size={14} /> Personal Outlays & Income Cash Flow
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {day.personalItems.map(item => (
                            <div key={item.id} className="bg-slate-50 p-3 rounded-xl border flex justify-between items-center text-xs">
                              <div className="min-w-0 flex-1 pr-4">
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-2 h-2 rounded-full ${item.type === 'income' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                  <p className="font-black text-slate-800 uppercase truncate text-xs">{item.title}</p>
                                </div>
                                <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Category: {item.category} {item.notes && <span className="lowercase font-normal text-slate-400">({item.notes})</span>}</p>
                              </div>
                              <div className={`font-black px-3 py-1 rounded-lg text-xs flex items-center gap-1 ${
                                item.type === 'income' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'
                              }`}>
                                {item.type === 'income' ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
                                ₹{Number(item.amount).toLocaleString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// --- MODULE LAYOUT ISOLATED REUSABLE CARD WRAPPERS ---
function ScopeStatCard({ title, val, color, desc }) {
  return (
    <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-xs flex flex-col justify-between transition-all hover:shadow-md">
      <div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
        <p className="text-[9px] text-slate-400 font-medium mt-0.5">{desc}</p>
      </div>
      <p className={`text-xl font-black ${color} mt-4`}>₹{Number(val || 0).toLocaleString()}</p>
    </div>
  );
}

function DayInlineMetric({ label, val, color }) {
  return (
    <div className="text-right">
      <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 leading-none">{label}</p>
      <p className={`text-xs font-black ${color} mt-0.5`}>₹{Number(val || 0).toLocaleString()}</p>
    </div>
  );
}